import { ErrorHandler, Injectable, inject } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import { CapabilityDefectReporter } from '../../../shared/services/capability-defect-reporter.service';
import {
  isRollupVendorKey,
  resolveVendor,
} from '../../../shared/services/vendor-channel-resolver.service';
import { AuthStore } from '../../auth.store';
import { fromMasked } from '../shared/masked-ean';
import { isoWeekStart } from './iso-week';
import { type RegionId, tryRegionId } from './region-id';
import { regionalCacheKey } from './regional-cache-key';
import type {
  RegionalSalesCellViolation,
  RegionalSalesDecodeFailure,
  RegionalSalesDecodeResult,
  RegionalSalesFact,
  RegionalSalesFeedViolation,
  RegionalSalesQuery,
  RegionalSalesViolation,
  RetailStore,
  VendorStores,
} from './regional-sales';
import {
  EMPTY_REGIONAL_SALES_READ,
  FAILED_STORE_INDEX_READ,
  type RegionalSalesReadResult,
  RegionalSalesRepository,
  type StoreIndexReadResult,
} from './regional-sales-repository';
import { tryVendorSlug } from './vendor-slug';

const INDEX_TABLE = 'portal_ret_index';
const AGG_TABLE = 'portal_ret_regional_agg';
const INDEX_COLUMNS = 'vendor, stores, updated_at';
/**
 * Only what is read. `cache_key` is the row's own address, already known to the caller that
 * looked the row up by it, and `product_filter` is the request echoed back; neither is
 * selected.
 */
const AGG_COLUMNS = 'payl, created_at';
const PAGE_SIZE = 1000;

/** What one skipped cell is called in the single report its kind gets per read. */
const CELL_VIOLATION_TEXT: Readonly<Record<RegionalSalesCellViolation, string>> = {
  'vendor-entry-not-an-object': 'vendor entr(ies) that are not an object',
  'region-key-empty': 'empty region key(s)',
  'region-entry-not-an-object': 'region entr(ies) that are not an object',
  'product-key-not-a-masked-ean': 'product key(s) that are not masked EANs',
  'product-entry-not-an-object': 'product entr(ies) that are not an object',
  'period-key-not-an-iso-week': 'period key(s) that are not ISO week labels',
  'cell-not-an-object': 'cell(s) that are not an object',
  'units-sold-not-a-number': 'cell(s) whose units sold is not a number',
  'stocking-stores-not-a-number': 'cell(s) whose stocking-store count is not a number',
};

/** What one broken response, rather than one cell, is called in the same report. */
const FEED_VIOLATION_TEXT: Readonly<Record<RegionalSalesFeedViolation, string>> = {
  'payload-not-an-object': 'payload(s) that are not an object',
  'vendor-key-not-a-chain-name': 'vendor key(s) that name no chain',
  'stores-column-not-an-object': 'store index(es) that are not an object',
  'store-entry-not-an-object': 'store entr(ies) that are not an object',
  'store-coordinates-not-numbers': 'store(s) whose coordinates are not numbers',
};

const VIOLATION_TEXT: Readonly<Record<RegionalSalesViolation, string>> = {
  ...CELL_VIOLATION_TEXT,
  ...FEED_VIOLATION_TEXT,
};

/**
 * The columns this ACL selects, as the schema types them. Only the `jsonb` blobs are
 * genuinely unstructured and get parsed below; the text columns are NOT NULL in the schema
 * and are trusted as such.
 */
type IndexRow = {
  readonly vendor: string;
  readonly stores: unknown;
  readonly updated_at: string | null;
};
type AggRow = {
  readonly payl: unknown;
  readonly created_at: string | null;
};

// ---------------------------------------------------------------------------
// Wire decoding -- everything below this line knows the feed's shapes
// ---------------------------------------------------------------------------

type VendorScope = Pick<RegionalSalesFact, 'vendor' | 'channel'>;
type RegionScope = VendorScope & Pick<RegionalSalesFact, 'region'>;
type ProductScope = RegionScope & Pick<RegionalSalesFact, 'ean'>;
type CellScope = ProductScope & Pick<RegionalSalesFact, 'week'>;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * The scope a failure can be reported against: always a chain, and however much deeper the
 * walk had got when it stopped.
 */
type FailureScope = VendorScope & Partial<Pick<RegionalSalesFact, 'region' | 'ean' | 'week'>>;

function failure(
  violation: RegionalSalesCellViolation,
  scope: FailureScope
): RegionalSalesDecodeFailure {
  return {
    kind: 'err',
    violation,
    vendor: scope.vendor,
    region: scope.region ?? null,
    ean: scope.ean ?? null,
    week: scope.week ?? null,
  };
}

/** The innermost cell: what one chain sold of one SKU in one region in one week. */
function decodeCell(raw: unknown, scope: CellScope): RegionalSalesDecodeResult {
  const cell = asRecord(raw);
  if (!cell) return failure('cell-not-an-object', scope);

  const unitsSold = asFiniteNumber(cell['qs']);
  if (unitsSold === null) return failure('units-sold-not-a-number', scope);

  const rawStocking = cell['sc'];
  const stockingStores =
    rawStocking === undefined || rawStocking === null ? undefined : asFiniteNumber(rawStocking);
  if (stockingStores === null) return failure('stocking-stores-not-a-number', scope);

  return { kind: 'ok', fact: { ...scope, unitsSold, stockingStores } };
}

/** One SKU's weeks, keyed by ISO week label. */
function decodeProduct(raw: unknown, scope: ProductScope): readonly RegionalSalesDecodeResult[] {
  const periods = asRecord(raw);
  if (!periods) return [failure('product-entry-not-an-object', scope)];

  const results: RegionalSalesDecodeResult[] = [];
  for (const [label, rawCell] of Object.entries(periods)) {
    const week = isoWeekStart(label);
    if (!week) {
      results.push(failure('period-key-not-an-iso-week', scope));
      continue;
    }
    results.push(decodeCell(rawCell, { ...scope, week }));
  }
  return results;
}

/**
 * Appends one level's results to the level above.
 *
 * A loop, not `push(...results)`: the spread passes every element as a call argument, and a
 * kommun-grain payload (290 regions x a brand's SKUs x a quarter of weeks) reaches the tens
 * of thousands, past which V8 throws RangeError. That would reject the whole read from
 * inside the decoder, losing the empty result and the violation report with it.
 */
function appendResults(
  into: RegionalSalesDecodeResult[],
  results: readonly RegionalSalesDecodeResult[]
): void {
  for (const result of results) into.push(result);
}

/** One region's SKUs. Every product key the feed stores is masked; an unmasked one is a
 * contract violation, reported and skipped rather than read as a real code. */
function decodeRegion(raw: unknown, scope: RegionScope): readonly RegionalSalesDecodeResult[] {
  const products = asRecord(raw);
  if (!products) return [failure('region-entry-not-an-object', scope)];

  const results: RegionalSalesDecodeResult[] = [];
  for (const [productKey, rawProduct] of Object.entries(products)) {
    const ean = fromMasked(productKey);
    if (!ean) {
      results.push(failure('product-key-not-a-masked-ean', scope));
      continue;
    }
    appendResults(results, decodeProduct(rawProduct, { ...scope, ean }));
  }
  return results;
}

/** One chain's regions. */
function decodeVendor(raw: unknown, scope: VendorScope): readonly RegionalSalesDecodeResult[] {
  const regions = asRecord(raw);
  if (!regions) return [failure('vendor-entry-not-an-object', scope)];

  const results: RegionalSalesDecodeResult[] = [];
  for (const [regionKey, rawRegion] of Object.entries(regions)) {
    const region: RegionId | null = tryRegionId(regionKey);
    if (!region) {
      results.push(failure('region-key-empty', scope));
      continue;
    }
    appendResults(results, decodeRegion(rawRegion, { ...scope, region }));
  }
  return results;
}

/**
 * `payl[vendor][region][product][isoWeek] = { qs, sc }`, and no further than this module.
 *
 * The chain a key resolves to is carried into every fact below it, and the domain builds
 * `VendorSlug`s out of those -- a cache key, a picker key, a donut label. A key the resolver
 * cannot strip a channel off leaves a value that constructor refuses, so the chain is skipped
 * and counted here rather than decoded into facts that detonate in a computed later.
 */
function decodePayload(payload: Record<string, unknown>): {
  readonly results: readonly RegionalSalesDecodeResult[];
  readonly violations: readonly RegionalSalesFeedViolation[];
} {
  const results: RegionalSalesDecodeResult[] = [];
  const violations: RegionalSalesFeedViolation[] = [];
  for (const [vendorKey, rawVendor] of Object.entries(payload)) {
    // Backend rollups are sums of the keys beside them; turning one into a fact would
    // double every total that reads them together.
    if (isRollupVendorKey(vendorKey)) continue;
    const resolved = resolveVendor(vendorKey);
    if (!tryVendorSlug(resolved.vendor)) {
      violations.push('vendor-key-not-a-chain-name');
      continue;
    }
    appendResults(results, decodeVendor(rawVendor, resolved));
  }
  return { results, violations };
}

/**
 * The column is typed `jsonb` but every row holds a JSON-encoded string inside it, so a reader has to be ready for both forms.
 */
function parsePayload(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'string') return asRecord(raw);
  try {
    return asRecord(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * One chain's store index: `stores[storeId] = { lat, lon, name, postcode }`.
 *
 * A `stores` column that is not an object at all and one bad entry inside it are reported
 * as different kinds: the first loses the chain's whole footprint, the second loses one
 * shop, and a report that cannot tell them apart reads the outage as a typo.
 */
function decodeStores(raw: unknown): {
  readonly stores: readonly RetailStore[];
  readonly violations: readonly RegionalSalesFeedViolation[];
} {
  const index = asRecord(raw);
  if (!index) return { stores: [], violations: ['stores-column-not-an-object'] };

  const stores: RetailStore[] = [];
  const violations: RegionalSalesFeedViolation[] = [];
  for (const [id, rawStore] of Object.entries(index)) {
    const store = asRecord(rawStore);
    if (!store) {
      violations.push('store-entry-not-an-object');
      continue;
    }
    const latitude = asFiniteNumber(store['lat']);
    const longitude = asFiniteNumber(store['lon']);
    // A store the map cannot place is not a store it can draw.
    if (latitude === null || longitude === null) {
      violations.push('store-coordinates-not-numbers');
      continue;
    }
    const name = store['name'];
    const postcode = store['postcode'];
    stores.push({
      id,
      name: typeof name === 'string' ? name : null,
      latitude,
      longitude,
      postcode: typeof postcode === 'string' ? postcode : null,
    });
  }
  return { stores, violations };
}

function violationsOf(results: readonly RegionalSalesDecodeResult[]): RegionalSalesCellViolation[] {
  return results
    .filter((r): r is RegionalSalesDecodeFailure => r.kind === 'err')
    .map((r) => r.violation);
}

/**
 * The successes, without a second array holding them: a brand-wide payload decodes
 * thousands of cells and the capability check only iterates what it is handed.
 */
function* decodedFacts(results: readonly RegionalSalesDecodeResult[]): Iterable<RegionalSalesFact> {
  for (const result of results) {
    if (result.kind === 'ok') yield result.fact;
  }
}

// ---------------------------------------------------------------------------
// Supabase implementation
// ---------------------------------------------------------------------------

@Injectable()
export class SupabaseRegionalSalesRepository extends RegionalSalesRepository {
  private readonly authStore = inject(AuthStore);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly defectReporter = inject(CapabilityDefectReporter);

  async readStoreIndex(): Promise<StoreIndexReadResult> {
    const supabase = await this.authStore.getSupabaseClient();
    const rows = await this.readIndexPages(supabase);
    if (rows === null) return FAILED_STORE_INDEX_READ;

    const vendors: VendorStores[] = [];
    const violations: RegionalSalesFeedViolation[] = [];
    for (const row of rows) {
      // Same reason as in the payload: a rollup row is the sum of the chains beside it, and
      // its stores are those chains' stores listed a second time.
      if (isRollupVendorKey(row.vendor)) continue;
      const resolved = resolveVendor(row.vendor);
      // And the same reason as in the payload for the chain name itself: the map keys a dot,
      // a picker checkbox and a donut slice off this value through `VendorSlug`.
      if (!tryVendorSlug(resolved.vendor)) {
        violations.push('vendor-key-not-a-chain-name');
        continue;
      }
      const decoded = decodeStores(row.stores);
      violations.push(...decoded.violations);
      vendors.push({
        ...resolved,
        stores: decoded.stores,
        updatedAt: row.updated_at,
      });
    }

    this.reportViolations(INDEX_TABLE, violations);
    this.defectReporter.report(INDEX_TABLE, vendors);
    return { failed: false, vendors };
  }

  async readFacts(query: RegionalSalesQuery): Promise<RegionalSalesReadResult> {
    const supabase = await this.authStore.getSupabaseClient();

    // One row addressed by its key: a single-row read cannot reach PostgREST's page cap,
    // so this is the one read here that does not page.
    const { data, error } = await supabase
      .from(AGG_TABLE)
      .select(AGG_COLUMNS)
      .eq('cache_key', regionalCacheKey(query))
      .limit(1);

    if (error) {
      this.errorHandler.handleError(
        new Error(`[RegionalSalesRepository] read of ${AGG_TABLE} failed: ${error.message}`)
      );
      return EMPTY_REGIONAL_SALES_READ;
    }

    const row = ((data ?? []) as AggRow[])[0];
    if (!row) return EMPTY_REGIONAL_SALES_READ;

    const payload = parsePayload(row.payl);
    if (!payload) {
      // A row whose payload will not parse is not an empty payload: reporting it as one
      // would tell the caller "aggregated, and there were no sales".
      this.reportViolations(AGG_TABLE, ['payload-not-an-object']);
      return { rowExists: true, hasPayload: false, createdAt: row.created_at, facts: [] };
    }

    const { results, violations } = decodePayload(payload);
    this.reportViolations(AGG_TABLE, [...violationsOf(results), ...violations]);
    this.defectReporter.report(AGG_TABLE, decodedFacts(results));
    return { rowExists: true, hasPayload: true, createdAt: row.created_at, facts: results };
  }

  /** Pages with .range() until a short page. Returns null once a page has errored. */
  private async readIndexPages(supabase: SupabaseClient): Promise<IndexRow[] | null> {
    const rows: IndexRow[] = [];
    let offset = 0;

    for (;;) {
      const { data, error } = await supabase
        .from(INDEX_TABLE)
        .select(INDEX_COLUMNS)
        .order('vendor', { ascending: true })
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) {
        this.errorHandler.handleError(
          new Error(`[RegionalSalesRepository] read of ${INDEX_TABLE} failed: ${error.message}`)
        );
        return null;
      }

      const page = (data ?? []) as IndexRow[];
      rows.push(...page);
      if (page.length < PAGE_SIZE) return rows;
      offset += PAGE_SIZE;
    }
  }

  /**
   * One report per read and violation kind, never per cell: a systemic format break in the
   * feed would otherwise be one Sentry event per cell, and a brand-wide payload holds
   * thousands of them.
   */
  private reportViolations(table: string, violations: readonly RegionalSalesViolation[]): void {
    const counts = new Map<RegionalSalesViolation, number>();
    for (const violation of violations) {
      counts.set(violation, (counts.get(violation) ?? 0) + 1);
    }
    for (const [violation, count] of counts) {
      this.errorHandler.handleError(
        new Error(
          `[RegionalSalesRepository] ${table}: skipped ${count} ${VIOLATION_TEXT[violation]}`
        )
      );
    }
  }
}
